import { NextPage, NextPageContext } from 'next'
import * as React from 'react'
import {
  fetchCompany,
  getCompanyContactPerson,
  getCompanyMemo,
  getCompanyStatus,
} from '@/lib/api/company'
import { fetchArticlesByCompany, fetchContentfulCompany } from '@/lib/company'
import {
  Article,
  ContentfulCompany,
  ContentfulNpStartupFloatingBanner,
  ContentfulNpStartupSlideArticle,
  ContentfulNpStartupStaticBanner,
  WithId,
} from '../../types'
import {
  CompanyContactPerson,
  CompanyData,
  CompanyMemo,
  GetCompanyContactPersonResult,
  GetCompanyMemoResult,
  GetCompanyStatusResult,
  OverseasCompanyData,
  Status,
} from '../../types/api'
import { useUser } from '../../contexts/AuthContext'
import { isTrial, isEnterprise, isLatestPlan } from '@/lib/user'
import CompanyPage from '@/components/pages/company/CompanyPage'
import OverseasCompanyPage from '@/components/pages/company/OverseasCompanyPage'
import { EnterpriseGuideTemplate } from '@/components/view/EnterpriseGuide'
import {
  getNpStartupFloatingBanner,
  getNpStartupSlideBannerArticles,
  getNpStartupStaticBanner,
} from '@/lib/api/contentful'
import { getSmartroundUrl } from '@/lib/api/smartround'

type Props = {
  company: Required<CompanyData | OverseasCompanyData> | null
  articles: Article[]
  oversea?: boolean
  slideBannerArticles: ContentfulNpStartupSlideArticle[] | null
  staticBanners: ContentfulNpStartupStaticBanner[] | null
  floatingBanner: ContentfulNpStartupFloatingBanner[] | null
  smartroundUrl: string | null
  companyMemo: CompanyMemo | null
  companyStatus: Status | null
  companyContactPerson: CompanyContactPerson | null
  reCaptchaEnabled: boolean
}

const CompanyShortnamePage: NextPage<Props> = (props) => {
  const user = useUser()

  return props.oversea ? (
    isTrial(user) || (isEnterprise(user) && isLatestPlan(user)) ? (
      <OverseasCompanyPage
        company={props.company as Required<OverseasCompanyData> | null}
      />
    ) : (
      <EnterpriseGuideTemplate />
    )
  ) : (
    <CompanyPage
      {...props}
      company={props.company as Required<CompanyData> | null}
    />
  )
}

CompanyShortnamePage.getInitialProps = async (ctx: NextPageContext) => {
  const { shortname } = ctx.query
  const company = await fetchCompany(ctx, shortname as string)
  const reCaptchaEnabled =
    ctx.req?.headers['x-datadog-synthetics-monitoring'] === 'true'
      ? false
      : true

  let articles: Article[] = []
  let slideBannerArticles: ContentfulNpStartupSlideArticle[] | null = null
  let contentfulCompany: WithId<ContentfulCompany> | null = null
  let staticBanners: ContentfulNpStartupStaticBanner[] | null = null
  let floatingBanner: ContentfulNpStartupFloatingBanner[] | null = null
  let smartroundUrl: string | null = null
  let companyMemo: GetCompanyMemoResult = { memo: null }
  let companyStatus: GetCompanyStatusResult = { companyStatus: null }
  let companyContactPerson: GetCompanyContactPersonResult = {
    contactPerson: null,
  }

  if (!company?.oversea) {
    ;[
      contentfulCompany,
      slideBannerArticles,
      staticBanners,
      floatingBanner,
      smartroundUrl,
      companyMemo,
      companyStatus,
      companyContactPerson,
    ] = await Promise.all([
      fetchContentfulCompany(shortname as string),
      getNpStartupSlideBannerArticles(),
      getNpStartupStaticBanner(),
      getNpStartupFloatingBanner(),
      getSmartroundUrl(ctx, company !== null ? company.corporateNumber : null),
      getCompanyMemo(ctx, shortname as string),
      getCompanyStatus(ctx, shortname as string),
      getCompanyContactPerson(ctx, shortname as string),
    ])
    if (contentfulCompany) {
      articles = await fetchArticlesByCompany(contentfulCompany.id)
    }
  }
  let isDomestic = true
  if (shortname !== undefined) {
    isDomestic = (shortname as string).startsWith('A-')
  }

  return {
    company,
    articles,
    oversea: !isDomestic,
    slideBannerArticles,
    staticBanners,
    floatingBanner,
    smartroundUrl,
    companyMemo: companyMemo.memo,
    companyStatus: companyStatus.companyStatus,
    companyContactPerson: companyContactPerson.contactPerson,
    reCaptchaEnabled,
  }
}

export default CompanyShortnamePage
